import {Approver} from '@domain/shared'
import {Box, Typography, List, ListItem, Alert, Stack} from '@mui/material'
import React, {FC, Fragment, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {COMMON_APPROVERS_WIDGET_NAMESPACE, STATUS_ALERT_PROPS} from '../consts'

type ApproversListProps = {
  approvers?: Approver[]
}

export const ApproversList: FC<ApproversListProps> = ({approvers}) => {
  const {t} = useTranslation(COMMON_APPROVERS_WIDGET_NAMESPACE)

  const grouped = useMemo(() => {
    if (!approvers) return []

    const groups = new Map<string, Approver[]>()

    for (const approver of approvers) {
      const key = approver.groupId ?? `no-group-${approver.user.id}`
      if (!groups.has(key)) {
        groups.set(key, [])
      }
      groups.get(key)!.push(approver)
    }

    return Array.from(groups.values())
  }, [approvers])

  return (
    <Box data-test-id="event-detail-approverName">
      <Typography variant="caption">
        {t('approvers.label.approver', {count: approvers?.length || 0})}
      </Typography>

      <Stack component={List} gap={1}>
        {grouped.length > 0
          ? grouped.map((group, idx) => (
              <ListItem
                key={group[0].groupId ?? idx}
                disablePadding
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 0.5
                }}
              >
                {group.map(({user, status}, index) => (
                  <Fragment key={user.id}>
                    <Alert
                      data-test-id={`event-detail-approver-${user.id}`}
                      icon={STATUS_ALERT_PROPS[status].getIcon()}
                      severity={STATUS_ALERT_PROPS[status].severity}
                      sx={{
                        px: 1,
                        py: 0,
                        alignItems: 'center',
                        whiteSpace: 'nowrap',
                        '& .MuiAlert-icon': {mr: 0.5}
                      }}
                    >
                      <Typography sx={{whiteSpace: 'nowrap'}}>{user.name}</Typography>
                    </Alert>
                    {group.length - 1 > index && <Typography>/</Typography>}
                  </Fragment>
                ))}
              </ListItem>
            ))
          : t('approvers.action.noneOfThat')}
      </Stack>
    </Box>
  )
}
